import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import api from '../utils/api';
import './Events.css';

const FALLBACK_IMG = '/assets/hero/hero-bg.webp';

function fmtDateRange(start, end) {
  if (!start) return '';
  const s = new Date(start);
  const e = end ? new Date(end) : s;
  const sameDay = s.toDateString() === e.toDateString();
  const opts = { day: 'numeric', month: 'short', year: 'numeric' };
  if (sameDay) return s.toLocaleDateString('en-IN', opts);
  // Same year, same month → "12–14 Apr 2026"
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${s.getDate()}–${e.getDate()} ${s.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-IN', opts)} – ${e.toLocaleDateString('en-IN', opts)}`;
}

function EventCard({ event }) {
  const price = Number(event.cost_inr) || 0;
  return (
    <Link to={`/events/${event.slug}`} className="event-card">
      <div className="event-card__image">
        <img src={event.image || FALLBACK_IMG} alt={event.title} loading="lazy" />
        <div className="event-card__date">{fmtDateRange(event.start_date, event.end_date)}</div>
      </div>
      <div className="event-card__body">
        <div className="event-card__meta">
          {event.venue && <span>📍 {event.venue}</span>}
          {event.capacity != null && (
            <span><strong>{Math.max(0, event.capacity - (event.seats_taken || 0))}</strong> seats left</span>
          )}
        </div>
        <h3 className="event-card__title">{event.title}</h3>
        {event.subtitle && <p className="event-card__subtitle">{event.subtitle}</p>}
        <div className="event-card__foot">
          <span className={`event-card__price ${price === 0 ? 'event-card__price--free' : ''}`}>
            {price > 0 ? `₹${price.toLocaleString('en-IN')}` : 'Free'}
          </span>
          <span className="event-card__cta">
            View details
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  );
}

function EventsPage() {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([api.getEvents(), api.getPastEvents()])
      .then(([up, pa]) => {
        if (!active) return;
        setUpcoming(Array.isArray(up) ? up : []);
        setPast(Array.isArray(pa) ? pa : []);
      })
      .catch(() => { /* show empty state */ })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // 'all' = upcoming (soonest first) followed by past (most recent first).
  // De-dupe by id in case an event happens to surface in both responses.
  const all = (() => {
    const seen = new Set();
    return [...upcoming, ...past].filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });
  })();

  const list = tab === 'upcoming' ? upcoming : tab === 'past' ? past : all;

  const emptyMessage = {
    all: 'No events to show yet — check back soon.',
    upcoming: 'No upcoming events right now — check back soon, or join our mailing list.',
    past: 'No past events to show yet.',
  }[tab];

  return (
    <>
      <PageBanner title="Events" />

      <section className="events-list">
        <div className="container">
          <div className="events-list__tabs">
            <button
              type="button"
              className={`events-list__tab ${tab === 'all' ? 'is-active' : ''}`}
              onClick={() => setTab('all')}
            >
              All ({all.length})
            </button>
            <button
              type="button"
              className={`events-list__tab ${tab === 'upcoming' ? 'is-active' : ''}`}
              onClick={() => setTab('upcoming')}
            >
              Upcoming ({upcoming.length})
            </button>
            <button
              type="button"
              className={`events-list__tab ${tab === 'past' ? 'is-active' : ''}`}
              onClick={() => setTab('past')}
            >
              Past ({past.length})
            </button>
          </div>

          {loading ? (
            <div className="events-list__empty">Loading events…</div>
          ) : list.length === 0 ? (
            <div className="events-list__empty">{emptyMessage}</div>
          ) : (
            <div className="events-list__grid">
              {list.map((e) => <EventCard key={e.id} event={e} />)}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default EventsPage;
